import { parse, stringify } from 'query-string';
import Routes from '@/routes';
import { LAUNCH_DARKLY_CLIENT_ID, LEGACY_HOST } from '@/environment';
import constants from '@/constants';
import storage from '@/storage';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { AccountDto, QUERY_MY_USER_ACCOUNT } from '@/graphql';
import * as Sentry from '@sentry/react';
import router from 'next/router';
import { identifyAuth0User } from '@/analytics';
import {
  ApplicationContextInterface,
  GrowFlowApolloClient,
  FeatureFlags,
} from './types';

// --------------------------------------------------------------------------
// Yes, this file might be large, I munged all the auth + account + featureFlags in here so that we can control the application init flow more imperatively.
// Why not declaratively? I think almost everything else in the app, including the routing should be declarative.  However, time has proven that this kind of code is much better when explicit.
// We needed a way to enter the 'auth'/'account' or 'featureFlags' flow from various places.
// Also, having all the application initialization 'near' each other physically in a file is easier for the new dev to understand.
// ^ Parsing the order of 'useEffect' hooks in distant context providers made the order in which these 3 very dependent states were set very confusing.
// Anyhow, if this file becomes a pain, I think it would do well to have its complexities split, but it still control this 'application' state.
//
// Entering the flow from any point 'initialize' -> 'authenticate' -> 'loadAccount' -> 'loadFeatureFlags'
// Will always attempt to finish the sequence. Thus, calling `loadAccount` always 'loadFeatureFlags'. Calling initialize will attempt to auth, load account and load feature flags.
//
// --------------------------------------------------------------------------

export const initializeApplication = async (
  applicationState: ApplicationContextInterface
): Promise<ApplicationContextInterface> => {
  try {
    const { state, code, jurisdictionId, accountId } =
      parse(window.location.search);

    applicationState.logout = () => {
      applicationState.apolloClient?.clearStore();
      applicationState.auth0Client?.logout({ returnTo: LEGACY_HOST });
    };

    // If we have hit the logout route directly, we should bail out + logout
    if (window.location.pathname.includes(Routes.logout)) {
      applicationState.logout();
      return applicationState;
    }

    if (jurisdictionId) {
      storage.setItem(constants.JURISDICTION_ID, jurisdictionId);
      removeQueryParam('jurisdictionId');
    }

    if (accountId) {
      storage.setItem(constants.ACCOUNT_ID, accountId);
      removeQueryParam('accountId');
    }

    if (code) {
      await applicationState.auth0Client.handleRedirectCallback();
      removeQueryParam('code');
      removeQueryParam('state');
    }

    if (state) {
      removeQueryParam('state');
    }

    applicationState.user = await applicationState.auth0Client?.getUser();

    if (applicationState.user) {
      identifyAuth0User(applicationState.user);

      try {
        applicationState.account = await loadAccount(
          applicationState.apolloClient
        );
      } catch (e) {
        // We hit a 400/exception fetching the user account (our first time touching GF Servers).
        // This could be from a malformed JURISDICTION_ID or ACCOUNT_ID
        // Clear these and force account reselection.
        storage.removeItem(constants.JURISDICTION_ID);
        storage.removeItem(constants.ACCOUNT_ID);

        await router.replace({
          pathname: Routes.switchLicense,
        });

        return applicationState;
      }
    } else {
      if (!window.location.pathname.includes(Routes.loginCallback)) {
        storage.setItem(
          constants.ROUTE_AFTER_AUTHENTICATION,
          window.location.href
        );
      }

      // If we could not authenticate, try to.
      applicationState.authenticating = true;
      applicationState.auth0Client.loginWithRedirect();
      return applicationState;
    }

    if (applicationState.account) {
      Sentry.setContext('Growflow Account', {
        account: applicationState.account.id,
        jurisdictionId: storage.getItem(constants.JURISDICTION_ID),
      });

      applicationState.featureFlags = await loadFeatureFlags(
        applicationState.account
      );
      applicationState.checkFlag = (flag): boolean =>
        !!(
          flag &&
          applicationState.featureFlags &&
          applicationState.featureFlags[flag] === true
        );
    } else {
      // Force user to the switch license route if no account is found.
      await router.replace({
        pathname: Routes.switchLicense,
      });

      return applicationState;
    }

    const routeAfterAuth = storage.getItem(
      constants.ROUTE_AFTER_AUTHENTICATION
    );

    if (routeAfterAuth) {
      storage.removeItem(constants.ROUTE_AFTER_AUTHENTICATION);

      const afterAuthUrl = new URL(routeAfterAuth);

      // Strip non-crucial auth-related params from original URL
      const { state, code, jurisdictionId, accountId, ...restQueryParams } = parse(afterAuthUrl.search);

      await router.replace({
        pathname: afterAuthUrl.pathname,
        query: restQueryParams,
      });
    }

    if (window.location.pathname.includes(Routes.loginCallback)) {
      await router.replace({
        pathname: Routes.index,
      });
    }

    return applicationState;
  } catch (e) {
    // Application initialization should always resolve, even if it resolves with an error.
    applicationState.error = e.message;

    Sentry.captureException(e);

    console.error(e);

    return applicationState;
  }
};

const hasLicenseSet = function (): boolean {
  return (
    !!storage.getItem(constants.JURISDICTION_ID) &&
    !!storage.getItem(constants.ACCOUNT_ID)
  );
};

const removeQueryParam = function (param: string) {
  const currentParams = parse(window.location.search);

  const updatedParams = stringify(
    {
      ...currentParams,
      [param]: null,
    },
    { skipNull: true }
  );

  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${updatedParams}`
  );
};

async function loadAccount(
  apolloClient?: GrowFlowApolloClient
): Promise<AccountDto | null> {
  if (!hasLicenseSet() || !apolloClient) {
    return null;
  }

  // Do not use the cache here, apollo does not know there is a new record set because we are only changing the headers.
  const results = await apolloClient.query({
    query: QUERY_MY_USER_ACCOUNT,
    errorPolicy: 'ignore',
    fetchPolicy: 'no-cache',
  });

  return results?.data?.myAccount || null;
}

async function loadFeatureFlags(account: AccountDto): Promise<FeatureFlags> {
  if (!account) {
    return {}
  }

  const { organization } = account;

  const client = LDClient.initialize(LAUNCH_DARKLY_CLIENT_ID, {
    key: organization?.id,
    name: account.name || '',
    custom: {
      name: account.name || '',
      organizationKey: organization?.organizationKey || '',
      organizationId: organization?.id,
      accountId: account.id,
      isPaid: account.isPaid ? 'true' : 'false',
      isStandAlone: account.isStandAlone,
      jurisdiction: account.vendor?.jurisdiction?.regionAbbreviation || '',
    }
  });

  return new Promise((resolve, reject) => {
    client.on('ready', () => {
      resolve(client.allFlags());
    });

    client.on('error', (e) => {
      reject(e);
    });
  });
}
